import { Phone, MapPin, Ruler, Armchair, DoorOpen, Hammer } from 'lucide-react';
import Seo from '../components/Seo';
import Reveal from '../components/Reveal';
import ArrowLink from '../components/ArrowLink';
import PackageCard from '../components/PackageCard';
import FaqAccordion from '../components/FaqAccordion';
import { IMAGES, PACKAGES } from '../data/content';

const NEEDS = [
  {
    icon: Ruler,
    title: 'Bespoke work needs proof of craft',
    text: 'A customer commissioning fitted wardrobes or a handmade staircase is buying your craftsmanship, not a commodity. Close-up joinery shots and finished-room photos show the workshop-quality finish that justifies your price.',
  },
  {
    icon: MapPin,
    title: 'You show up for “carpenter near me”',
    text: 'A complete Google Business Profile with the right categories, service areas and photos is what puts a local carpenter on the map. We set it up properly and guide you through Google’s verification.',
  },
  {
    icon: Phone,
    title: 'Serious customers can reach you in seconds',
    text: 'People planning bespoke joinery research carefully, then contact the makers who look the part. Click-to-call, WhatsApp and a simple enquiry form on every page mean that enquiry lands with you.',
  },
];

const WORK_TYPES = [
  {
    icon: Armchair,
    title: 'Fitted furniture and alcoves',
    text: 'Wardrobes, shelving and media units — the work customers scroll Instagram dreaming about. A gallery of crisp fitted projects turns that browsing into enquiries.',
  },
  {
    icon: DoorOpen,
    title: 'Doors, stairs and second-fix',
    text: 'The skilled second-fix work where precision shows. Photos of tight mitres and clean finishes quietly tell customers you’re not the average chippy.',
  },
  {
    icon: Hammer,
    title: 'Kitchens and larger projects',
    text: 'Full installations and renovation joinery — higher-value work won on trust, reviews and a portfolio that shows you’ve done it properly before.',
  },
];

const CARPENTER_FAQS = [
  {
    q: 'I do bespoke work, not standard jobs. Can a website get that across?',
    a: 'That’s exactly who this suits best. Bespoke joinery is sold on proof of craft — so your site is built around a portfolio: close-up joinery details, finished installations, and the story of how each piece was made to measure. Customers see the difference before they ever ask your day rate.',
  },
  {
    q: 'My customers often find me on Instagram. Isn’t that enough?',
    a: 'Instagram is where they browse; a website is where they commit. A proper site gives your best work a permanent, professional home that Google can find, lets customers enquire properly, and makes you look established rather than hobby-level. We set both up so they work together.',
  },
  {
    q: 'I mix fitted furniture with general carpentry. Can the site cover both?',
    a: 'Yes. Your services are laid out so each customer finds what applies to them — the homeowner wanting alcove units on one hand, the one needing a staircase or kitchen fitted on the other. The Built to Grow package gives you up to ten pages, plenty of room for both.',
  },
  {
    q: 'I’m in the workshop or on site all day. How much of my time does this take?',
    a: 'About an hour in total, spread across the build: one plain-English questionnaire, your project photos from your phone, and a look at the finished site before launch. Everything else is handled for you.',
  },
];

export default function CarpentersPage() {
  return (
    <>
      <Seo
        title="Websites for Carpenters & Joiners — Show Your Craftsmanship | Build My Trade"
        description="Professional websites, Google Business Profile setup and social pages built specifically for UK carpenters and joiners. Fixed packages from £249, plain English, everything handled."
        path="/websites-for-carpenters"
        image={IMAGES.carpentry.url}
      />

      {/* Hero */}
      <section className="bg-charcoal bg-blueprint" data-testid="carpenters-hero">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-4 pb-24 pt-32 sm:px-6 sm:pt-40 lg:grid-cols-2 lg:px-8">
          <div>
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-lime">
              Websites for carpenters &amp; joiners
            </p>
            <h1 className="mt-5 text-balance font-heading text-4xl font-extrabold leading-[1.08] tracking-tight text-offwhite sm:text-5xl lg:text-6xl">
              Craftsmanship this good shouldn’t be <span className="text-lime">your best-kept secret.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-offwhite/70 sm:text-lg">
              Build My Trade sets up everything a carpenter or joiner needs to win bespoke work: a professional
              website with a portfolio that shows your workshop-quality finish, a complete Google Business Profile
              and branded social pages — for one fixed, published price.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <ArrowLink to="/pricing" variant="lime" testid="carpenters-cta-packages" className="px-7 py-3.5">
                See Fixed-Price Packages
              </ArrowLink>
              <ArrowLink to="/contact" variant="outline-light" testid="carpenters-cta-quote" className="px-7 py-3.5">
                Get a Free Quote
              </ArrowLink>
            </div>
            <p className="mt-7 text-sm text-offwhite/50">
              Straightforward packages. Clear prices. No marketing jargon.
            </p>
          </div>
          <Reveal>
            <img
              src={IMAGES.carpentry.url}
              alt={IMAGES.carpentry.alt}
              loading="eager"
              className="aspect-[4/3] w-full rounded-2xl border border-charcoal-line object-cover"
            />
          </Reveal>
        </div>
      </section>

      {/* What a carpenter's online presence needs */}
      <section className="bg-offwhite" data-testid="carpenters-needs">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <Reveal className="max-w-3xl">
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-stonebrand">
              Built around how bespoke work is won
            </p>
            <h2 className="mt-4 text-balance font-heading text-2xl font-bold leading-snug tracking-tight text-charcoal sm:text-3xl lg:text-4xl">
              What a carpenter’s website has to do
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-5 lg:grid-cols-3">
            {NEEDS.map((n, i) => (
              <Reveal key={i} delay={0.08 * i} className="h-full">
                <div className="h-full rounded-2xl border border-hairline bg-white p-8">
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-charcoal">
                    <n.icon className="h-5 w-5 text-lime" aria-hidden="true" />
                  </span>
                  <h3 className="mt-6 font-heading text-lg font-bold tracking-tight text-charcoal">{n.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-stonebrand">{n.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Work types */}
      <section className="border-y border-hairline bg-white" data-testid="carpenters-work-types">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <div className="grid items-start gap-12 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <Reveal>
                <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-stonebrand">
                  From fitted alcoves to full installations
                </p>
                <h2 className="mt-4 text-balance font-heading text-2xl font-bold leading-snug tracking-tight text-charcoal sm:text-3xl lg:text-4xl">
                  Set up to win the work your craft deserves
                </h2>
                <p className="mt-5 text-base leading-relaxed text-stonebrand">
                  Customers don’t always know the difference between a joiner and a general chippy — until they see
                  the work. Your website makes the difference obvious, with the detail shots and finished projects
                  that command proper prices.
                </p>
              </Reveal>
              <Reveal delay={0.15} className="mt-8">
                <img
                  src={IMAGES.workshop.url}
                  alt={IMAGES.workshop.alt}
                  loading="lazy"
                  className="aspect-[4/3] w-full rounded-2xl object-cover"
                />
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <ul className="space-y-5">
                {WORK_TYPES.map((w, i) => (
                  <Reveal key={i} delay={0.08 * i}>
                    <li className="flex gap-5 rounded-2xl border border-hairline bg-offwhite-light p-7">
                      <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-lime">
                        <w.icon className="h-5 w-5 text-charcoal" aria-hidden="true" />
                      </span>
                      <div>
                        <h3 className="font-heading text-lg font-bold tracking-tight text-charcoal">{w.title}</h3>
                        <p className="mt-1.5 text-sm leading-relaxed text-stonebrand">{w.text}</p>
                      </div>
                    </li>
                  </Reveal>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Packages */}
      <section className="bg-charcoal" data-testid="carpenters-packages">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <Reveal className="max-w-3xl">
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-lime">Fixed, published prices</p>
            <h2 className="mt-4 text-balance font-heading text-2xl font-bold leading-snug tracking-tight text-offwhite sm:text-3xl lg:text-4xl">
              Packages that fit a carpentry business
            </h2>
            <p className="mt-5 text-base leading-relaxed text-offwhite/65">
              The same straightforward packages we offer every trade — one-off payments, everything listed, nothing
              hidden. Carpenters and joiners with a strong portfolio tend to choose Built to Grow: up to ten pages,
              an expanded gallery and a tailored logo that looks right on an invoice and a van.
            </p>
          </Reveal>
          <div className="mt-16 grid items-stretch gap-6 lg:grid-cols-3">
            {PACKAGES.map((pkg, i) => (
              <PackageCard key={pkg.slug} pkg={pkg} delay={0.08 * i} />
            ))}
          </div>
        </div>
      </section>

      {/* Carpenter FAQs */}
      <section className="bg-offwhite" data-testid="carpenters-faqs">
        <div className="mx-auto max-w-5xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <Reveal className="max-w-3xl">
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-stonebrand">
              Questions carpenters ask us
            </p>
            <h2 className="mt-4 text-balance font-heading text-2xl font-bold leading-snug tracking-tight text-charcoal sm:text-3xl lg:text-4xl">
              Straight answers for busy carpenters
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="mt-10">
            <FaqAccordion items={CARPENTER_FAQS} testid="carpenters-faq" />
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-charcoal-deep bg-blueprint" data-testid="carpenters-cta">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <div className="mx-auto max-w-2xl text-center">
            <Reveal>
              <h2 className="text-balance font-heading text-3xl font-extrabold tracking-tight text-offwhite sm:text-4xl">
                Ready for customers who <span className="text-lime">value proper craftsmanship?</span>
              </h2>
              <p className="mt-5 text-base leading-relaxed text-offwhite/65">
                Tell us about your carpentry or joinery business and the work you want more of — we’ll recommend the
                right starting point, in plain English, with no obligation.
              </p>
              <div className="mt-9">
                <ArrowLink to="/contact" variant="lime" testid="carpenters-cta-final" className="px-8 py-4">
                  Get Your Free Quote
                </ArrowLink>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
